/**
 * CreatorReactionStrip — collapsed read-only reaction counts for the card
 * creator on CardDetail.
 *
 * iter-78: per the spec, creators should see how their card is landing
 * publicly — but they can't (and shouldn't) react to their own card.
 * This is a 1-line, low-chrome counter strip with three quiet pills:
 *
 *   ❤ 12  felt this   🕯 4  held space   👁 89  opens
 *
 * Renders nothing until the card has a public slug AND at least one of
 * the counters is > 0 (otherwise it'd just be three sad zeros). Polls
 * every 30s while the page is foregrounded.
 *
 * Hooks into the existing /api/public/c/{slug} payload which already
 * returns aggregate `reactions` counts (felt + candle) for free.
 */
import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Heart, Flame, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

export default function CreatorReactionStrip({ slug, opens }) {
    const [counts, setCounts] = useState({ felt: 0, candle: 0 });
    const [loaded, setLoaded] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!slug) return;
        let alive = true;

        const fetchCounts = async () => {
            try {
                const { data } = await axios.get(`${API}/public/c/${slug}`);
                if (!alive) return;
                if (data?.reactions) {
                    setCounts({
                        felt: data.reactions.felt || 0,
                        candle: data.reactions.candle || 0,
                    });
                }
                setLoaded(true);
            } catch {
                if (alive) setLoaded(true);
            }
        };
        fetchCounts();

        // Re-poll while tab is foregrounded only — avoids waste in background.
        timerRef.current = setInterval(() => {
            if (document.visibilityState === "visible") fetchCounts();
        }, POLL_MS);
        return () => {
            alive = false;
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [slug]);

    // Don't bother rendering before we have data — avoids a 0/0/0 flash.
    if (!loaded) return null;
    const opensCount = typeof opens === "number" ? opens : 0;
    const anyActivity = counts.felt > 0 || counts.candle > 0 || opensCount > 0;
    if (!anyActivity) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden mt-4 flex items-center justify-center gap-2"
            data-testid="creator-reaction-strip"
        >
            <Pill icon={Heart} label="felt this" count={counts.felt} testid="creator-felt-count" />
            <Pill icon={Flame} label="held" count={counts.candle} testid="creator-candle-count" />
            {opensCount > 0 && (
                <Pill icon={Eye} label="opens" count={opensCount} testid="creator-opens-count" />
            )}
        </motion.div>
    );
}

function Pill({ icon: Icon, label, count, testid }) {
    return (
        <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/8"
            data-testid={testid}
        >
            <Icon className="w-3 h-3 text-white/55" strokeWidth={1.6} />
            <span className="text-[12px] font-medium text-white/90">{formatCount(count)}</span>
            <span className="text-[9px] tracking-[0.18em] uppercase text-white/40">{label}</span>
        </div>
    );
}

function formatCount(n) {
    if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}
